// Provider Directory — facilities (hospitals, practices, referrers) and
// physicians, each with a generated reference number (F001 / D001), plus a
// client's care team (client_physicians) and the review queue that holds
// anything typed on a form that could not be linked with confidence.
//
// Rules the UI leans on:
//   - physicians.npi is unique: the same person cannot be created twice.
//   - A client has exactly one current PCP and at most one current ordering
//     physician (partial unique indexes); specialists are unlimited.
//   - Parents suggest, coordinators confirm: nothing typed on a public form
//     writes here directly — it comes through directory_review_queue.
//   - NPPES (the federal NPI registry) settles *who*; staff evidence settles
//     *where to fax*. Registry contact details are suggestions, shown beside
//     what we have, never a silent overwrite.
import { supabase } from './supabase';

export const FACILITY_TYPES = [
  { value: 'hospital',           label: 'Hospital' },
  { value: 'specialty_clinic',   label: 'Specialty clinic' },
  { value: 'pediatric_practice', label: 'Pediatric practice' },
  { value: 'referrer',           label: 'Referrer' },
  { value: 'other',              label: 'Other' },
];
export const FACILITY_TYPE_LABEL = Object.fromEntries(FACILITY_TYPES.map(t => [t.value, t.label]));

export const CARE_ROLES = [
  { value: 'pcp',        label: 'PCP',        hint: 'Primary care — one per client' },
  { value: 'ordering',   label: 'Ordering',   hint: 'Signs orders, CMNs and the GAPP packet — one per client' },
  { value: 'specialist', label: 'Specialist', hint: 'Any number' },
  { value: 'referring',  label: 'Referring',  hint: 'Sent the client to us' },
  { value: 'other',      label: 'Other',      hint: '' },
];
export const CARE_ROLE_LABEL = Object.fromEntries(CARE_ROLES.map(r => [r.value, r.label]));

export const CONTACT_ROUTES = [
  { value: '',       label: '—' },
  { value: 'fax',    label: 'Fax' },
  { value: 'phone',  label: 'Phone' },
  { value: 'email',  label: 'Email' },
  { value: 'portal', label: 'Portal' },
];

// ---------------------------------------------------------------- format
export const digits = (s) => String(s ?? '').replace(/\D/g, '');
export const tenDigits = (s) => { let d = digits(s); if (d.length === 11 && d.startsWith('1')) d = d.slice(1); return d.length === 10 ? d : null; };
export const fmtPhone = (s) => { const d = tenDigits(s); return d ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (s || ''); };
export const physicianName = (p) => p ? [p.last_name, p.first_name].filter(Boolean).join(', ') + (p.credentials ? `, ${p.credentials}` : '') : '';
export const facilityAddress = (f) => f ? [f.address_line1, f.address_line2, [f.city, f.state].filter(Boolean).join(', '), f.zip].filter(Boolean).join(', ') : '';

/** NPI check digit: Luhn over the 10 digits with the 80840 prefix. */
export const isValidNpi = (s) => {
  const d = digits(s);
  if (d.length !== 10) return false;
  const full = '80840' + d;
  let sum = 0, dbl = false;
  for (let i = full.length - 1; i >= 0; i--) {
    let n = Number(full[i]);
    if (dbl) { n *= 2; if (n > 9) n -= 9; }
    sum += n; dbl = !dbl;
  }
  return sum % 10 === 0;
};

const cleanContact = (row) => ({
  ...row,
  npi:   row.npi ? digits(row.npi) || null : null,
  phone: row.phone ? tenDigits(row.phone) : null,
  fax:   row.fax ? tenDigits(row.fax) : null,
  email: row.email?.trim().toLowerCase() || null,
  preferred_contact: row.preferred_contact || null,
});

// ------------------------------------------------------------- facilities
export const listFacilities = () =>
  supabase.from('facility_directory').select('*').order('name');

export const getFacility = (id) =>
  supabase.from('facility_directory').select('*').eq('id', id).single();

export const saveFacility = async (form, actor) => {
  const { id, ref_code, parent_name, physician_count, client_count, created_at, updated_at, created_by, ...rest } = form;
  const row = cleanContact(rest);
  row.records_fax = row.records_fax ? tenDigits(row.records_fax) : null;
  row.updated_by = actor;
  row.state = (row.state || 'GA').toUpperCase();
  row.zip = digits(row.zip).slice(0, 5) || null;
  if (row.npi && !isValidNpi(row.npi)) return { error: { message: 'That NPI fails the check digit — please re-check it.' } };
  if (!row.name?.trim()) return { error: { message: 'A facility needs a name.' } };
  row.name = row.name.trim();
  if (id) return supabase.from('facilities').update(row).eq('id', id).select('*').single();
  row.created_by = actor;
  return supabase.from('facilities').insert(row).select('*').single();
};

export const setFacilityStatus = (id, status, actor) =>
  supabase.from('facilities').update({ status, updated_by: actor }).eq('id', id);

// ------------------------------------------------------------- physicians
export const listPhysicians = () =>
  supabase.from('physician_directory').select('*').order('last_name').order('first_name');

/** What a public form may see: active physicians' names and practices, nothing else. */
export const listPublicPhysicians = () =>
  supabase.from('public_physician_list').select('*').order('last_name').order('first_name');

export const getPhysician = (id) =>
  supabase.from('physician_directory').select('*').eq('id', id).single();

/**
 * The doctor's own contact fields. physician_directory fills phone, fax, email
 * and best route from the site (and the fax from the parent organisation)
 * where the doctor has none; an edit form must start from these instead, or
 * saving copies the inherited value onto the doctor.
 */
export const getPhysicianOwnContact = (id) =>
  supabase.from('physicians').select('phone, fax, email, preferred_contact').eq('id', id).single();

/** Typeahead: name, NPI, facility, specialty. Small list, so filter client-side. */
export const searchPhysicians = (rows, q) => {
  const s = (q || '').trim().toLowerCase();
  if (!s) return rows;
  const d = digits(s);
  return rows.filter(p =>
    (p.display_name || '').toLowerCase().includes(s) ||
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(s) ||
    (p.facility_name || '').toLowerCase().includes(s) ||
    (p.specialty || '').toLowerCase().includes(s) ||
    (p.ref_code || '').toLowerCase() === s ||
    (d.length >= 4 && ((p.npi || '').includes(d) || (p.phone || '').includes(d) || (p.fax || '').includes(d))));
};

export const savePhysician = async (form, actor) => {
  const { id, ref_code, display_name, facility_ref, facility_name, facility_type, address_line1, address_line2, city, state, zip,
    client_count, created_at, updated_at, created_by, nppes_checked_at, nppes_data, ...rest } = form;
  const row = cleanContact(rest);
  row.updated_by = actor;
  row.first_name = (row.first_name || '').trim();
  row.last_name = (row.last_name || '').trim();
  row.credentials = row.credentials?.trim().replace(/\./g, '').toUpperCase() || null;
  row.specialty = row.specialty?.trim() || null;
  row.facility_id = row.facility_id || null;
  if (!row.last_name) return { error: { message: 'A physician needs at least a last name.' } };
  if (row.npi && !isValidNpi(row.npi)) return { error: { message: 'That NPI fails the check digit — please re-check it.' } };
  if (nppes_data !== undefined) { row.nppes_data = nppes_data; row.nppes_checked_at = nppes_checked_at ?? new Date().toISOString(); }
  if (id) return supabase.from('physicians').update(row).eq('id', id).select('*').single();
  row.created_by = actor;
  const res = await supabase.from('physicians').insert(row).select('*').single();
  if (res.error && /physicians_npi_uidx|duplicate/i.test(res.error.message)) {
    return { error: { message: `NPI ${row.npi} is already in the directory — search for it instead of adding again.` } };
  }
  return res;
};

export const setPhysicianStatus = (id, status, actor) =>
  supabase.from('physicians').update({ status, updated_by: actor }).eq('id', id);

export const markVerified = (table, id, actor) =>
  supabase.from(table).update({ verified_at: new Date().toISOString(), verified_by: actor, updated_by: actor }).eq('id', id);

/** Fold a physician's links into another and retire the duplicate. */
export const mergePhysicians = async (fromId, intoId, actor) => {
  const { data: links } = await supabase.from('client_physicians').select('id, client_id, role').eq('physician_id', fromId);
  for (const l of links || []) {
    const { error } = await supabase.from('client_physicians').update({ physician_id: intoId }).eq('id', l.id);
    if (error && /duplicate|unique/i.test(error.message)) await supabase.from('client_physicians').delete().eq('id', l.id);
    else if (error) return { error };
  }
  for (const [table, col] of [['intake_submissions', 'physician_id'], ['dme_orders', 'physician_id'], ['dme_cmns', 'physician_id'], ['cmn_records', 'physician_id'], ['leads', 'referring_physician_id']]) {
    await supabase.from(table).update({ [col]: intoId }).eq(col, fromId);
  }
  await supabase.from('directory_review_queue').update({ physician_id: intoId }).eq('physician_id', fromId);
  return supabase.from('physicians').update({ status: 'inactive', updated_by: actor, notes: `Merged into another record by ${actor} on ${new Date().toLocaleDateString()}` }).eq('id', fromId);
};

// -------------------------------------------------------------- care team
export const getCareTeam = (clientId) =>
  supabase.from('client_physicians')
    .select('id, client_id, physician_id, role, started_on, ended_on, notes, created_at')
    .eq('client_id', clientId).is('ended_on', null).order('created_at');

/** Care team with the physician directory row attached (two queries; the view has no FK for PostgREST to follow). */
export const getCareTeamDetailed = async (clientId) => {
  const { data: links, error } = await getCareTeam(clientId);
  if (error) return { data: null, error };
  const ids = [...new Set((links || []).map(l => l.physician_id))];
  if (!ids.length) return { data: [], error: null };
  const { data: docs, error: e2 } = await supabase.from('physician_directory').select('*').in('id', ids);
  if (e2) return { data: null, error: e2 };
  const byId = Object.fromEntries((docs || []).map(d => [d.id, d]));
  return { data: (links || []).map(l => ({ ...l, physician: byId[l.physician_id] || null })), error: null };
};
export const addCareTeamMember = async (clientId, physicianId, role, actor) => {
  const res = await supabase.from('client_physicians').insert({ client_id: clientId, physician_id: physicianId, role, created_by: actor }).select('*').single();
  if (res.error && /one_pcp|one_ordering/i.test(res.error.message)) {
    return { error: { message: role === 'pcp' ? 'This client already has a PCP. Change the existing one instead.' : 'This client already has an ordering physician. Change the existing one instead.' } };
  }
  if (res.error && /duplicate|unique/i.test(res.error.message)) return { error: { message: 'That physician is already on the care team with this role.' } };
  return res;
};

export const setCareTeamRole = async (linkId, role) => {
  const res = await supabase.from('client_physicians').update({ role }).eq('id', linkId);
  if (res.error && /one_pcp|one_ordering/i.test(res.error.message)) {
    return { error: { message: role === 'pcp' ? 'This client already has a PCP.' : 'This client already has an ordering physician.' } };
  }
  return res;
};

export const removeCareTeamMember = (linkId) =>
  supabase.from('client_physicians').delete().eq('id', linkId);

/** Every client a physician touches — for the record panel. */
export const getPhysicianClients = (physicianId) =>
  supabase.from('client_physicians')
    .select('id, role, ended_on, client:clients(id, client_id, first_name, last_name, current_status)')
    .eq('physician_id', physicianId).is('ended_on', null).order('role');

export const getPhysicianFaxes = (fax, limit = 8) =>
  fax ? supabase.from('faxes').select('id, created_at, request_title, doc_type, faxage_status, client:clients(client_id, first_name, last_name)')
    .eq('direction', 'outbound').eq('recipient_fax', fax).order('created_at', { ascending: false }).limit(limit)
    : Promise.resolve({ data: [], error: null });

// ------------------------------------------------------------ fax routes
// Which fax a doctor or facility is sent to is decided in one place, the
// directory_fax_routes view: a doctor's own fax, else their site's, else the
// parent organisation's; and for a records request, the site's records fax,
// else the parent's. That is how every CHOA site and doctor is offered CHOA's
// Health Information Services fax without it being copied onto each of them.
// physician_directory.fax already reads from the view; nothing else restates
// the rule.
export const listFaxRoutes = () =>
  supabase.from('directory_fax_routes').select('kind, id, fax, fax_from, records_fax, organisation_name');

/** Route rows keyed `physician:<id>` / `facility:<id>`. */
export const faxRouteIndex = (rows) => new Map((rows || []).map(r => [`${r.kind}:${r.id}`, r]));

/** The fax to offer: for purpose 'records' the records fax when there is one, otherwise the ordinary fax. */
export const faxFor = (route, purpose) => !route ? ''
  : (purpose === 'records' && route.records_fax) ? route.records_fax
  : (route.fax || '');

// --------------------------------------------------------------- NPPES
async function callFn(name, body) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || process.env.REACT_APP_SUPABASE_ANON_KEY}`, apikey: process.env.REACT_APP_SUPABASE_ANON_KEY },
    body: JSON.stringify(body || {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.error || `${name} failed (${res.status})`);
  return json;
}
export const nppesLookupNpi = (npi) => callFn('nppes-lookup', { npi: digits(npi) }).then(r => r.match);
export const nppesSearchName = (last_name, first_name, state = 'GA') => callFn('nppes-lookup', { last_name, first_name: first_name || undefined, state }).then(r => r.matches || []);
export const nppesSearchOrg = (organization, state = 'GA') => callFn('nppes-lookup', { organization, state }).then(r => r.matches || []);

/** Admin: the one-time backfill. dry_run by default. */
export const runDirectoryBackfill = (opts = {}) => callFn('provider-directory-backfill', opts);

// ----------------------------------------------------------- review queue
export const listReviewQueue = (status = 'open') =>
  supabase.from('directory_review_queue').select('*').eq('status', status).order('created_at');

export const countOpenReviews = () =>
  supabase.from('directory_review_queue').select('id', { count: 'exact', head: true }).eq('status', 'open');

const LINK_COLUMN = { intake_submissions: 'physician_id', dme_orders: 'physician_id', dme_cmns: 'physician_id', cmn_records: 'physician_id', leads: 'referring_physician_id' };

/**
 * Resolve a queue item by pointing its source at a physician. For a client
 * row, that means a care-team link in the typed role; for anything else, the
 * link column on the source row. `status` is 'linked' (existing physician)
 * or 'created' (a new record was added first).
 */
export const resolveReview = async (item, physicianId, actor, status = 'linked', note = null) => {
  if (item.source_table === 'clients') {
    const role = ['pcp', 'ordering', 'specialist', 'referring'].includes(item.source_role) ? item.source_role : (item.source_role === 'prescribing' ? 'ordering' : 'other');
    const res = await addCareTeamMember(item.source_id, physicianId, role, actor);
    if (res.error && !/already on the care team/.test(res.error.message)) return res;
  } else if (item.source_table === 'physicians') {
    // a queued question about the physician record itself (fax conflict, NPI choice) — nothing to link
  } else if (LINK_COLUMN[item.source_table]) {
    const { error } = await supabase.from(item.source_table).update({ [LINK_COLUMN[item.source_table]]: physicianId }).eq('id', item.source_id);
    if (error) return { error };
  }
  return supabase.from('directory_review_queue').update({ status, physician_id: physicianId, resolved_at: new Date().toISOString(), resolved_by: actor, resolution_note: note }).eq('id', item.id);
};

export const dismissReview = (item, actor, note) =>
  supabase.from('directory_review_queue').update({ status: 'dismissed', resolved_at: new Date().toISOString(), resolved_by: actor, resolution_note: note || null }).eq('id', item.id);

/** Queue a physician typed on a public form (intake / referral) for a coordinator. */
export const queueTypedPhysician = (source_table, source_id, source_role, source_label, raw, reason = 'Typed on a form; not yet matched to the directory.') =>
  supabase.from('directory_review_queue').upsert({ source_table, source_id, source_role, source_label, raw, reason, suggestions: [] }, { onConflict: 'source_table,source_id,source_role', ignoreDuplicates: true });
